import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AppConfigProvider } from '@app/app.config.provider';
import { IRecalculationRequest } from '@models/recalculation/recalculation-request';
import { IRecalculationTask } from '@models/recalculation/recalculation-task';
import { RecalculationTrigger, RecalculationTriggerName } from '@models/recalculation/recalculation-trigger';
import { ToastService } from '@services/global/toast.service';
import { Toast } from 'angular2-toaster';
import { Observable, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

const INFO_NOTIFICATION = (title: string, body = ''): Toast => {
  return {
    title: title.trim(),
    body: body.trim(),
    type: 'info',
    showCloseButton: true
  };
};
// const SUCCESS_NOTIFICATION = (title: string, body = ''): Toast => {
//   return {
//     title: title.trim(),
//     body: body.trim(),
//     type: 'success',
//     showCloseButton: true
//   };
// };
const ERROR_NOTIFICATION = (title: string, body = ''): Toast => {
  return {
    title: title.trim(),
    body: body.trim(),
    type: 'error',
    showCloseButton: true
  };
};

@Injectable({
  providedIn: 'root'
})
export class RecalculationService {
  private RECALCULATION_API: string;
  public RECALCULATION_API_STREAM: string;
  private RECALCULATION_API_POLL: string;
  public STREAM_TIMEOUT = 5000;
  public STREAM_TIMEOUT_LONG = 30000;

  public isRecalculationInProgress = false;
  public recalculationInProgress = {};

  constructor(
    private readonly http: HttpClient,
    private readonly appConfig: AppConfigProvider,
    private readonly notificationService: ToastService
  ) {
    this.appConfig.environment.subscribe(env => {
      this.RECALCULATION_API = `${env.API.RECALCULATION}/api/calculate`;
      this.RECALCULATION_API_STREAM = `${env.API.RECALCULATION}/api/calculate/tasks-stream`;
      this.RECALCULATION_API_POLL = `${this.RECALCULATION_API}/pollimportrecalc`;
    });
  }

  public showInProgressToastAfterProductSetupSave() {
    const title = `Re-aggregation and Re-calculation in progress ...`;
    const id = 'MUST_BE_UNIQUE';
    const toast = INFO_NOTIFICATION(title, `Initiated by: ${RecalculationTriggerName[RecalculationTrigger.ProductDefinitionChange]}`);
    toast.toastId = id;
    this.notificationService.hideToast(id);
    this.notificationService.showToast(toast);
  }

  public startCalculation(request: IRecalculationRequest, trigger: string = null): Observable<IRecalculationTask[]> {
    if (!trigger) {
      trigger = RecalculationTriggerName[request.RecalculationTrigger];
    }

    return this.http.post(this.RECALCULATION_API, request).pipe(
      tap((tasks: IRecalculationTask[]) => {
        this.updateIsRecalculationInProgress(tasks);
        this.notificationService.showToast(INFO_NOTIFICATION(`Recalculation in progress ...`, `Initiated by: ${trigger}`));
      }),
      catchError(err => {
        this.notificationService.showToast(ERROR_NOTIFICATION(`Recalculation failed!`, `Initiated by: ${trigger}`));
        return throwError(err);
      })
    );
  }

  public getRecalculationTasks(): Observable<IRecalculationTask[]> {
    return this.http.get<IRecalculationTask[]>(this.RECALCULATION_API);
  }

  public updateDurations(tasks: IRecalculationTask[]): IRecalculationTask[] {
    this.updateIsRecalculationInProgress(tasks);
    const requestIds = Array.from(new Set(tasks.map(t => t.RequestId)));
    requestIds.forEach(requestId => this.updateIsRecalculationInProgressForRequest(tasks, requestId));
    return tasks;
  }

  // For all Requests
  private updateIsRecalculationInProgress(tasks: IRecalculationTask[]) {
    this.isRecalculationInProgress = !!tasks.find(task => task.EndDateTime === null && !task.HasError);

    // Update all started tasks' durations
    const nowSecs = new Date().getTime();
    tasks
      .filter(task => task.StartDateTime)
      .forEach(task => {
        const endSecs = task.EndDateTime ? new Date(task.EndDateTime).getTime() : nowSecs;
        task.Duration = Math.ceil((endSecs - new Date(task.StartDateTime).getTime()) / 1000);
      });
  }

  // For a single Request
  private updateIsRecalculationInProgressForRequest(tasks: IRecalculationTask[], requestId: number) {
    const progress = !!tasks.find(task => task.RequestId === requestId && task.EndDateTime === null && !task.HasError);
    this.recalculationInProgress[requestId] = progress;
  }

  public pollImportRecalculation() {
    this.http.get(this.RECALCULATION_API_POLL).subscribe();
  }
}
